/**
 * inviteService — calls Cloud Functions for user management operations
 * that require Firebase Admin SDK (invite, disable, import).
 *
 * All calls include the current user's ID token in the Authorization header
 * so the Cloud Function can verify the caller is an admin.
 */
import { auth } from '../config/firebase';
import type { InvitePayload, ImportResult } from '../types';

const INVITE_URL  = import.meta.env.VITE_INVITE_USER_FUNCTION_URL  || '';
const DISABLE_URL = import.meta.env.VITE_DISABLE_USER_FUNCTION_URL || '';
const IMPORT_URL  = import.meta.env.VITE_IMPORT_USERS_FUNCTION_URL  || '';

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function callFunction<T>(url: string, body: unknown): Promise<T> {
  const idToken = await getIdToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
  return data as T;
}

// ─── Invite a single user ─────────────────────────────────────────────────────

export async function inviteUser(payload: InvitePayload): Promise<{ uid: string; isCompany: boolean }> {
  return callFunction(INVITE_URL, payload);
}

// ─── Disable / Re-enable a Firebase Auth account ─────────────────────────────

export async function disableUserAccount(uid: string, disabled: boolean): Promise<void> {
  await callFunction(DISABLE_URL, { uid, disabled });
}

// ─── Bulk import users ────────────────────────────────────────────────────────

export async function importUsers(users: InvitePayload[], sendEmail = true): Promise<ImportResult> {
  return callFunction<ImportResult>(IMPORT_URL, { users, sendEmail });
}
